<template>
  <div>
    <v-container
      fluid
      class="mx-auto mt-4 h-100"
      :style="{ width: smAndDown ? '100%' : (searchPageResultsSnippetNote ? '80%' : '60%') }"
      data-locator="search-page"
    >
      <DocumentSearchFiltersPane
        v-model="isFiltersPaneOpen"
        :lastNewQuery="lastNewQuery"
        @update:model-value="toggleFiltersPane($event)"
        @update:filters="handleFiltersUpdate($event)"
      />
      <v-row>
        <v-col cols="12">
          <v-row class="mb-4 align-center" justify="space-between">
            <template v-if="searchPageResultsPending">
              <v-skeleton-loader
                class="w-100"
                type="heading"
              ></v-skeleton-loader>
            </template>
            <template v-else>
              <div class="d-flex align-center">
                <app-button
                  class="mr-2"
                  type="icon"
                  size="small"
                  data-locator="search-page-back-button"
                  @click="$router.back()"
                >
                  <app-material-icon name="arrow_back" />
                </app-button>
                <span class="font-weight-medium text--medium">
                  {{ $t('views.DocumentSearchPage.title', { query }, searchPageResultsPagination.itemCount) }}
                </span>
              </div>
              <a :href="$t('views.DocumentSearchPage.needHelpUrl')" target="_blank" class="text-decoration-none" style="color: #2671F2">
                {{ $t('views.DocumentSearchPage.needHelp') }}
              </a>
            </template>
          </v-row>

          <v-row class="mb-4">
            <template v-if="searchPageResultsPending">
              <v-skeleton-loader
                type="text"
                height="40"
                width="100"
              />
            </template>

            <app-button
              v-else-if="searchPageResults.length !== 0"
              type="outlined"
              variant="neutral"
              data-locator="filters-button"
              @click="toggleFiltersPane(true)">
              <template #left-icon>
                <app-material-icon name="filter_list" />
              </template>
              {{ $t('common.filters') }}
            </app-button>
          </v-row>

          <v-row v-if="appliedFilters.length > 0"
                 class="mb-4 align-center"
                 cols="12"
          >
            <template v-if="searchPageResultsPending">
              <v-skeleton-loader
                type="text"
                height="40"
                width="200"
              />
            </template>
            <template v-else>
              <span class="font-weight-medium text--medium mr-2">
              {{ $t('views.DocumentSearchPage.activeFilters') }}
            </span>
              <v-chip v-for="filterKey in appliedFilters"
                      :key="filterKey"
                      class="filter-chip mr-2"
                      variant="flat"
                      color="white"
              >
                <template #prepend>
                  <app-material-icon name="close" size="xs" class="mr-1" @click="handleFilterRemoving(filterKey)" />
                </template>
                <span class="text--small">{{ $t(`views.DocumentSearchPage.filtersLabels.${filterKey}`) }}</span>
              </v-chip>
            </template>
          </v-row>

          <DocumentSearchPageLoader
            v-if="searchPageResultsPending"
            data-locator="search-page-loading"
          />

          <template v-else>
            <DocumentSearchEmptyState v-if="searchPageResults.length === 0" />
            <v-row v-else>
              <DocumentSearchPageSnippetNote
                v-if="smAndDown && searchPageResultsSnippetNote"
                class="mb-2"
                data-locator="snippet-note"
              />

              <v-col cols="12" :md="searchPageResultsSnippetNote ? 8 : 12">
                <v-card
                  v-for="result in searchPageResults"
                  :key="result.id"
                  class="mb-4"
                >
                  <AppBarDocumentSearchResultsItem
                    class="py-4 pl-2 pr-3"
                    :file="{ ...result.source, highlight: result.highlight }"
                    variant="page"
                  />
                </v-card>
                <div class="d-flex justify-center">
                  <AppPagination
                    class="my-4"
                    :currentPage="currentPage"
                    :pageSize="SEARCH_PAGE_PAGE_SIZE"
                    :total="searchPageResultsPagination.itemCount"
                    :hide-page-size="true"
                    :data-locator="'search-page-pagination'"
                    @update:currentPage="onPageChange"
                  />
                </div>
              </v-col>
              <v-col v-if="!smAndDown && searchPageResultsSnippetNote" md="4">
                <DocumentSearchPageSnippetNote data-locator="snippet-note"/>
              </v-col>
            </v-row>
          </template>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import { useDisplay } from 'vuetify'
import { mapActions, mapMutations, mapState } from 'vuex'

import AppBarDocumentSearchResultsItem from '@/common/app-bar/AppBarDocumentSearchResultsItem.vue'
import DocumentSearchEmptyState from '@/common/document-search/DocumentSearchEmptyState.vue'
import DocumentSearchFiltersPane from "@/common/document-search/DocumentSearchFiltersPane.vue"
import AppPagination from '@/common/pagination/AppPagination.vue'
import { HOME_LAYOUT_ROUTE_NAMES } from '@/router/routerData'
import { SEARCH_PAGE_SEARCH } from '@/store/modules/documents/action_types'
import { SET_SEARCH_PAGE_FILTERS, SET_SEARCH_PAGE_APPLIED_FILTERS } from "@/store/modules/documents/mutation_types";
import { GlobalMutationTypes } from '@/store/modules/global/mutation_types'
import DocumentSearchPageSnippetNote from '@/views/DocumentSearchPageSnippetNote.vue'

import DocumentSearchPageLoader from './DocumentSearchPageLoader.vue'


// Based on a LexisNexis guideline
const SEARCH_PAGE_PAGE_SIZE = 30

export default defineComponent({
  name: 'DocumentSearchPage',
  components: {
    DocumentSearchFiltersPane,
    DocumentSearchPageLoader,
    AppBarDocumentSearchResultsItem,
    AppPagination,
    DocumentSearchEmptyState,
    DocumentSearchPageSnippetNote,
  },
  props: {
    mnemo: {
      type: String,
      default: '',
    },
  },
  setup() {
    const { smAndDown } = useDisplay()
    return { smAndDown }
  },
  data () {
    return {
      currentPage: 1,
      SEARCH_PAGE_PAGE_SIZE,
      isFiltersPaneOpen: false,
      lastNewQuery: '',
    }
  },
  computed: {
    ...mapState('global', ['routerHistory', 'homeLayout']),
    ...mapState('documents', [
      'searchPageResults',
      'searchPageResultsPending',
      'searchPageResultsPagination',
      'searchPageResultsSnippetNote',
      'searchPageFilters',
      'searchPageAppliedFilters',
    ]),
    query(): string {
      return (this.$route.query['query'] as string) || ''
    },
    needHelpLinkUrl () {
      const baseUrl = 'https://help.closd.com/'
      if (this.$i18n.locale === 'fr') {
        return `${baseUrl}fr/knowledge-base/rechercher-un-document-avec-la-barre-de-recherche-globale/`
      }
      return `${baseUrl}en/knowledge-base/searching-for-a-document-with-the-global-search-bar/`
    },
    appliedFilters () {
      return Object.keys(this.searchPageAppliedFilters).filter((key => this.searchPageAppliedFilters[key].length > 0))
    },
  },
  async created () {
    if (
      this.routerHistory.length !== 0 &&
      HOME_LAYOUT_ROUTE_NAMES.includes(this.routerHistory[this.routerHistory.length - 1].name as string)
    ) {
      this.SET_HOME_LAYOUT(true)
    }

    await this.SEARCH_PAGE_SEARCH({
      query: this.query,
      page: 1,
      pageSize: SEARCH_PAGE_PAGE_SIZE,
    })

    this.lastNewQuery = this.query
  },
  unmounted () {
    // Clean up after ourselves if we set it.
    if (this.homeLayout) {
      this.SET_HOME_LAYOUT(false)
    }
  },
  watch: {
    async query (newQuery: string, oldQuery: string) {
      if (newQuery !== oldQuery) {
        this.currentPage = 1
        await this.SEARCH_PAGE_SEARCH({
          query: newQuery,
          page: 1,
          pageSize: SEARCH_PAGE_PAGE_SIZE,
        })
        this.lastNewQuery = newQuery
      }
    },
  },
  methods: {
    ...mapActions('documents', [SEARCH_PAGE_SEARCH]),
    ...mapMutations('global', [GlobalMutationTypes.SET_HOME_LAYOUT]),
    ...mapMutations('documents', [SET_SEARCH_PAGE_FILTERS, SET_SEARCH_PAGE_APPLIED_FILTERS]),
    onPageChange(page: number) {
      this.launchSearchQuery(page)
    },
    toggleFiltersPane(value: boolean) {
      this.isFiltersPaneOpen = value
    },
    handleFiltersUpdate(filters: Record<string, any>) {
      this.launchSearchQuery(1, filters)
    },
    handleFilterRemoving(filterKey: string) {
      const updatedFilters = { ...this.searchPageAppliedFilters,  [filterKey]: []}

      this.SET_SEARCH_PAGE_FILTERS(updatedFilters)
      this.SET_SEARCH_PAGE_APPLIED_FILTERS(updatedFilters)

      this.launchSearchQuery(1, updatedFilters)
    },
    launchSearchQuery(page: number, filters?: Record<string, any>) {
      this.currentPage = page

      this.SEARCH_PAGE_SEARCH({
        query: this.query,
        page,
        pageSize: SEARCH_PAGE_PAGE_SIZE,
        ...(filters && { filters }),
      })
    },
  },
})
</script>

<style lang="scss" scoped>
.filter-chip {
  border: 1px solid rgb(var(--v-theme-grey-200));
}
</style>
